/**
 * NameTemplateUtils
 * Helper functions and maps used by NameTemplate.
 */

import { useMemo } from 'react'
import { i18n } from '@fs/zion-locale'

export const templateToLanguagesMap = {
  ar: ['ar', 'ar-Latn'],
  bg: ['bg', 'bg-Latn'],
  cs: ['cs'],
  da: ['da'],
  de: ['de'],
  el: ['el', 'el-Latn'],
  en: ['en'],
  es: ['es'],
  et: ['et'],
  fa: ['fa', 'fa-Latn'],
  fi: ['fi'],
  fr: ['fr'],
  hr: ['hr'],
  hu: ['hu'],
  hy: ['hy', 'hy-Latn'],
  id: ['id'],
  it: ['it'],
  ja: ['ja', 'ja-Hrkt', 'ja-Latn'],
  km: ['km', 'km-Latn'],
  ka: ['ka', 'ka-Latn'],
  ko: ['ko-Hang', 'ko-Hani', 'ko-Latn'],
  lt: ['lt'],
  lv: ['lv'],
  mg: ['mg'],
  mn: ['mn-Cyrl', 'mn-Latn'],
  ms: ['ms'],
  nl: ['nl'],
  no: ['no'],
  pl: ['pl'],
  pt: ['pt'],
  ro: ['ro'],
  ru: ['ru', 'ru-Latn'],
  sk: ['sk'],
  sm: ['sm'],
  sq: ['sq'],
  sv: ['sv'],
  sw: ['sw'],
  th: ['th', 'th-Latn'],
  tl: ['tl'],
  to: ['to'],
  tr: ['tr'],
  uk: ['uk', 'uk-Latn'],
  und: ['und'],
  x: ['x-Latn'],
  vi: ['vi'],
  zh: ['zh', 'zh-Hani', 'zh-Latn'],
}

export const otherLastNameStrings = {
  'mn-Cyrl': 'name-template.family-surname',
  'mn-Latn': 'name-template.family-surname',
}

export const languageToScriptMap = {
  ar: 'ARAB',
  'ar-Latn': 'ROMAN',
  bg: 'CYRILLIC',
  'bg-Latn': 'ROMAN',
  cs: 'ROMAN',
  da: 'ROMAN',
  de: 'ROMAN',
  el: 'GREEK',
  'el-Latn': 'ROMAN',
  en: 'ROMAN',
  es: 'ROMAN',
  et: 'ROMAN',
  fa: 'ARAB',
  'fa-Latn': 'ROMAN',
  fi: 'ROMAN',
  fr: 'ROMAN',
  id: 'ROMAN',
  hr: 'ROMAN',
  hu: 'ROMAN',
  hy: 'ARMENIAN',
  'hy-Latn': 'ROMAN',
  it: 'ROMAN',
  ja: 'CHINESE-KANJI',
  'ja-Hrkt': 'KANA',
  'ja-Latn': 'ROMAN',
  ka: 'GEOR',
  'ka-Latn': 'ROMAN',
  km: 'KHMER',
  'km-Latn': 'ROMAN',
  'ko-Hang': 'HANGUL',
  'ko-Hani': 'CHINESE-HANJA',
  'ko-Latn': 'ROMAN',
  lt: 'ROMAN',
  lv: 'ROMAN',
  mg: 'ROMAN',
  'mn-Cyrl': 'MONGOLIAN',
  'mn-Latn': 'ROMAN',
  ms: 'ROMAN',
  nl: 'ROMAN',
  no: 'ROMAN',
  pl: 'ROMAN',
  pt: 'ROMAN',
  ro: 'ROMAN',
  ru: 'CYRILLIC',
  'ru-Latn': 'ROMAN',
  sk: 'ROMAN',
  sm: 'ROMAN',
  sq: 'ROMAN',
  sv: 'ROMAN',
  sw: 'ROMAN',
  th: 'THAI',
  'th-Latn': 'ROMAN',
  tl: 'ROMAN',
  to: 'ROMAN',
  tr: 'ROMAN',
  uk: 'CYRILLIC',
  'uk-Latn': 'ROMAN',
  und: 'ROMAN',
  vi: 'VIETNAMESE',
  zh: 'CHINESE-HAN',
  'zh-Hani': 'CHINESE-HAN',
  'zh-Latn': 'ROMAN',
}

const scriptTranslationKeyMap = {
  ARAB: 'name-template.ARAB',
  CYRILLIC: 'name-template.CYRILLIC',
  ROMAN: 'name-template.ROMAN',
  GREEK: 'name-template.GREEK',
  ARMENIAN: 'name-template.ARMENIAN',
  'CHINESE-KANJI': 'name-template.CHINESE-KANJI',
  GEOR: 'name-template.GEOR',
  KANA: 'name-template.KANA',
  KHMER: 'name-template.KHMER',
  HANGUL: 'name-template.HANGUL',
  'CHINESE-HANJA': 'name-template.CHINESE-HANJA',
  MONGOLIAN: 'name-template.MONGOLIAN',
  THAI: 'name-template.THAI',
  VIETNAMESE: 'name-template.VIETNAMESE',
  'CHINESE-HAN': 'name-template.CHINESE-HAN',
}

export const sinotypicTemplates = ['hu', 'ja', 'km', 'ko', 'mn', 'ro', 'to', 'vi', 'zh']
export const templatesWithoutTitle = ['ja', 'ko', 'vi', 'zh']
export const templatesWithoutSuffix = ['ar', 'th', 'mn']
export const templatesWithoutLastNameDescription = ['zh', 'ko']

export const langSupportsTitle = (lang) => !templatesWithoutTitle.includes(lang.split('-')[0])
export const langSupportsSuffix = (lang) => !templatesWithoutSuffix.includes(lang.split('-')[0])
export const langSupportsLastNameDescription = (lang) =>
  !templatesWithoutLastNameDescription.includes(lang?.split('-')?.[0])

export function localeIsASupportedTemplate(locale) {
  return Object.keys(templateToLanguagesMap).includes(locale)
}

export function getTemplateLanguages(template) {
  const filtered = templateToLanguagesMap[template]
  return filtered || []
}

export const langToTransliterationScriptMap = {
  bg: 'cyrillic-russian',
  el: 'greek',
  'ja-Hrkt': 'katakana',
  hy: 'armenian',
  km: 'khmer',
  'ko-Hang': 'hangul',
  'mn-Cyrl': 'cyrillic-mongolian',
  ru: 'cyrillic-russian',
  th: 'thai',
  uk: 'cyrillic-russian',
  zh: 'hanzi-mandarin-pinyin',
  'zh-Hani': 'hanzi-mandarin-pinyin',
}

export const nameTemplateNamespace = 'nameTemplate'
export const fieldNames = {
  given: 'firstName',
  surname: 'lastName',
  title: 'title',
  suffix: 'suffix',
}

export const useNameTemplateOptions = () => {
  return useMemo(
    () => [
      { value: 'ar', label: i18n.t('name-template.arabic'), 'data-test-lang': 'ARABIC' },
      { value: 'sq', label: i18n.t('name-template.albanian'), 'data-test-lang': 'ALBANIAN' },
      { value: 'hy', label: i18n.t('name-template.ARMENIAN'), 'data-test-lang': 'ARMENIAN' },
      { value: 'bg', label: i18n.t('name-template.bulgarian'), 'data-test-lang': 'BULGARIAN' },
      { value: 'zh', label: i18n.t('name-template.chinese'), 'data-test-lang': 'CHINESEP' },
      { value: 'hr', label: i18n.t('name-template.croatian'), 'data-test-lang': 'CROATIAN' },
      { value: 'cs', label: i18n.t('name-template.czech'), 'data-test-lang': 'CZECH' },
      { value: 'da', label: i18n.t('name-template.danish'), 'data-test-lang': 'DANISH' },
      { value: 'nl', label: i18n.t('name-template.dutch'), 'data-test-lang': 'DUTCH' },
      { value: 'en', label: i18n.t('name-template.english'), 'data-test-lang': 'ENGLISH' },
      { value: 'et', label: i18n.t('name-template.estonian'), 'data-test-lang': 'ESTONIAN' },
      { value: 'fi', label: i18n.t('name-template.finnish'), 'data-test-lang': 'FINNISH' },
      { value: 'fr', label: i18n.t('name-template.french'), 'data-test-lang': 'FRENCH' },
      { value: 'ka', label: i18n.t('name-template.georgian'), 'data-test-lang': 'GEORGIAN' },
      { value: 'de', label: i18n.t('name-template.german'), 'data-test-lang': 'GERMAN' },
      { value: 'el', label: i18n.t('name-template.GREEK'), 'data-test-lang': 'GREEK' },
      { value: 'hu', label: i18n.t('name-template.hungarian'), 'data-test-lang': 'HUNGARIAN' },
      { value: 'id', label: i18n.t('name-template.indonesian'), 'data-test-lang': 'INDONESIAN' },
      { value: 'it', label: i18n.t('name-template.italian'), 'data-test-lang': 'ITALIAN' },
      { value: 'ja', label: i18n.t('name-template.japanese'), 'data-test-lang': 'JAPANESE' },
      { value: 'km', label: i18n.t('name-template.KHMER'), 'data-test-lang': 'KHMER' },
      { value: 'ko', label: i18n.t('name-template.korean'), 'data-test-lang': 'KOREAN' },
      { value: 'lv', label: i18n.t('name-template.latvian'), 'data-test-lang': 'LATVIAN' },
      {
        value: 'lt',
        label: i18n.t('name-template.lithuanian'),
        'data-test-lang': 'LITHUANIAN',
      },
      { value: 'mg', label: i18n.t('name-template.malagasy'), 'data-test-lang': 'MALAGASY' },
      { value: 'ms', label: i18n.t('name-template.malay'), 'data-test-lang': 'MALAY' },
      { value: 'mn', label: i18n.t('name-template.MONGOLIAN'), 'data-test-lang': 'MONGOLIAN' },
      { value: 'no', label: i18n.t('name-template.norwegian'), 'data-test-lang': 'NORWEGIAN' },
      { value: 'fa', label: i18n.t('name-template.farsi'), 'data-test-lang': 'FARSI' },
      { value: 'pl', label: i18n.t('name-template.polish'), 'data-test-lang': 'POLISH' },
      { value: 'pt', label: i18n.t('name-template.portuguese'), 'data-test-lang': 'PORTUGUESE' },
      { value: 'ro', label: i18n.t('name-template.romanian'), 'data-test-lang': 'ROMANIAN' },
      { value: 'ru', label: i18n.t('name-template.russian'), 'data-test-lang': 'RUSSIAN' },
      { value: 'sm', label: i18n.t('name-template.samoan'), 'data-test-lang': 'SAMOAN' },
      { value: 'sk', label: i18n.t('name-template.slovak'), 'data-test-lang': 'SLOVAK' },
      { value: 'es', label: i18n.t('name-template.spanish'), 'data-test-lang': 'SPANISH' },
      { value: 'sw', label: i18n.t('name-template.swahili'), 'data-test-lang': 'SWAHILI' },
      { value: 'sv', label: i18n.t('name-template.swedish'), 'data-test-lang': 'SWEDISH' },
      { value: 'tl', label: i18n.t('name-template.tagalog'), 'data-test-lang': 'TAGALOG' },
      { value: 'th', label: i18n.t('name-template.THAI'), 'data-test-lang': 'THAI' },
      { value: 'to', label: i18n.t('name-template.tongan'), 'data-test-lang': 'TONGAN' },
      { value: 'tr', label: i18n.t('name-template.turkish'), 'data-test-lang': 'TURKISH' },
      { value: 'uk', label: i18n.t('name-template.ukrainian'), 'data-test-lang': 'UKRAINIAN' },
      { value: 'vi', label: i18n.t('name-template.VIETNAMESE'), 'data-test-lang': 'VIETNAMESE' },
      { value: 'und', label: i18n.t('name-template.other'), 'data-test-lang': 'OTHER' },
    ],
    []
  )
}

export const useLanguageTitle = (lang) => {
  return useMemo(() => {
    if (languageToScriptMap[lang]) {
      return i18n.t(scriptTranslationKeyMap[languageToScriptMap[lang]])
    }
    if (lang === 'und-Latn' || lang === 'x-Latn') {
      return i18n.t('name-template.ROMAN')
    }
    return lang
  }, [lang])
}
